import React, { useState, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * useConfirm — in-page confirmation dialog.
 * Returns [confirm, ConfirmUI]. Call `await confirm({title, description})` to get true/false.
 * Render <ConfirmUI /> somewhere in the component tree.
 */
export function useConfirm() {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState({
    title: "¿Estás seguro?",
    description: "Esta acción no se puede deshacer.",
    confirmLabel: "Eliminar",
    cancelLabel: "Cancelar",
    danger: true,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((opts = {}) => {
    setContent((c) => ({ ...c, ...opts }));
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handle = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  const ConfirmUI = (
    <AlertDialog open={open} onOpenChange={(o) => !o && handle(false)}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{content.title}</AlertDialogTitle>
          <AlertDialogDescription>{content.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => handle(false)} data-testid="confirm-cancel">
            {content.cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => handle(true)}
            className={
              content.danger
                ? "bg-red-600 dark:bg-red-500 text-white border-0 hover:bg-red-700 dark:hover:bg-red-600"
                : ""
            }
            data-testid="confirm-ok"
          >
            {content.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return [confirm, ConfirmUI];
}
